import {ChatComposer} from '@github-ui/agent-sessions-interface/components/ChatComposer'
import {useOverlayDirection} from '@github-ui/copilot-chat-contexts/OverlayDirectionContext'
import {clsx} from 'clsx'
import type {ComponentProps} from 'react'

import {
  ChatModelControls,
  ChatSpaceControl,
  type ChatModelControlsProps,
  type ChatSpaceControlProps,
} from './ChatComposerControls'

type ChatComposerFrameProps =
  ComponentProps<typeof ChatComposer> extends infer Props
    ? Props extends unknown
      ? Omit<Props, 'attachMenuContent' | 'toolbarContent'>
      : never
    : never
export type ChatPromptFrameProps = ChatComposerFrameProps & {
  modelControls: ChatModelControlsProps
  spaceControl: ChatSpaceControlProps
  toolbarClassName?: string
}

/**
 * The shared Chat prompt frame. Lifecycle controllers retain their own
 * transport, queue, retry, persistence, and attachment semantics; this frame
 * owns the presentation they genuinely share: the prompt input and the
 * model/reasoning/Space control arrangement.
 */
export function ChatPromptFrame({
  modelControls,
  spaceControl,
  toolbarClassName,
  ...composerProps
}: ChatPromptFrameProps) {
  const preferredOverlaySide = useOverlayDirection()
  const tooltipDirection =
    composerProps.tooltipDirection ??
    (preferredOverlaySide === 'outside-top' || preferredOverlaySide === 'inside-top' ? 'n' : undefined)
  // A read-only surface can still be read, but nothing on it may be changed.
  // `ChatComposer` already drops the attach menu (and so the Space control) in
  // that state; the model and reasoning pickers are ours to withhold, and
  // leaving them mounted would offer edits the surface cannot accept.
  const readOnly = composerProps.readOnly ?? false
  const controls = readOnly ? undefined : <ChatModelControls {...modelControls} tooltipDirection={tooltipDirection} />

  return (
    <ChatComposer
      {...composerProps}
      tooltipDirection={tooltipDirection}
      attachMenuContent={readOnly ? undefined : <ChatSpaceControl {...spaceControl} />}
      toolbarContent={
        controls && toolbarClassName ? <div className={clsx(toolbarClassName)}>{controls}</div> : controls
      }
    />
  )
}

ChatPromptFrame.displayName = 'ChatPromptFrame'
